import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/apiClient";
import AppShell from "@/components/AppShell";
import { LetterCard } from "@/components/letters/LetterCard";
import { FactsForm } from "@/components/letters/FactsForm";
import { GeneratedLetter } from "@/components/letters/GeneratedLetter";

const GROUPS = [[1, "Round 1 — Initial factual disputes"], [2, "Round 2 — Investigation follow-up"], [3, "Round 3 — Evidence and escalation"], ["special", "Special requests"]];
const sel = "w-full min-h-[44px] rounded-xl border border-slate-200 bg-white px-3 text-sm";

export default function LetterLibrary() {
  const [params] = useSearchParams();
  const [reports, setReports] = useState([]);
  const [reportId, setReportId] = useState(params.get("report") || "");
  const [accountId, setAccountId] = useState(params.get("account") || "");
  const [lib, setLib] = useState(null);
  const [card, setCard] = useState(null);
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);

  useEffect(() => { api.get("/reports").then((r) => { setReports(r.data); if (!reportId && r.data[0]) setReportId(r.data[0].id); }).catch(() => {}); }, []); // eslint-disable-line
  const report = reports.find((r) => r.id === reportId);
  const accounts = useMemo(() => [...(report?.accounts || [])].sort((a, b) => Number(b.is_negative) - Number(a.is_negative)), [report]);
  const account = accounts.find((a) => a.id === accountId);
  useEffect(() => { if (report && !account && accounts[0]) setAccountId(accounts[0].id); }, [report, account, accounts]);
  useEffect(() => {
    if (!reportId || !accountId) return;
    api.get("/letter-library", { params: { report_id: reportId, account_id: accountId } }).then((r) => setLib(r.data)).catch(() => setLib(null));
  }, [reportId, accountId, doc]);

  const generate = async (facts, length) => {
    setBusy(true); setErrors([]);
    try {
      setDoc((await api.post("/letter-library/generate", { letter_key: card.letter_key, report_id: reportId, account_id: accountId, facts, length })).data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErrors(d?.errors || d?.unmet || [d?.message || (typeof d === "string" ? d : "Could not generate this letter.")]);
    } finally { setBusy(false); }
  };

  const rec = lib?.cards.find((c) => c.letter_key === lib.recommended);
  return (
    <AppShell>
      <div className="eyebrow">Letter Library</div>
      <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 mt-1" data-testid="letters-heading">Choose Your Letter</h1>
      <p className="mt-2 text-sm sm:text-base text-slate-600">Pick the account, then use the letter that fits your situation. We build it only from facts you confirm.</p>
      {reports.length === 0 && <p className="mt-6 text-sm text-slate-500" data-testid="letters-no-report">Upload a credit report first to use the Letter Library.</p>}
      {reports.length > 0 && !doc && !card && (
        <div className="mt-6 grid sm:grid-cols-2 gap-2">
          <select className={sel} value={reportId} onChange={(e) => { setReportId(e.target.value); setAccountId(""); }} data-testid="letters-report-select">
            {reports.map((r) => <option key={r.id} value={r.id}>{r.original_filename || "Credit report"}</option>)}
          </select>
          <select className={sel} value={accountId} onChange={(e) => setAccountId(e.target.value)} data-testid="letters-account-select">
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.creditor} {a.account_number_masked} {a.is_negative ? "· negative" : ""}</option>)}
          </select>
        </div>
      )}
      {doc ? (
        <div className="mt-6"><GeneratedLetter doc={doc} setDoc={setDoc} lookups={lib.lookups} recommended={doc.library_key === lib.recommended} onBack={() => { setDoc(null); setCard(null); }} structures={lib.cards.find((c) => c.letter_key === doc.library_key)?.structures || [doc.library.structure]} /></div>
      ) : card ? (
        <div className="mt-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-3" data-testid="facts-letter-name">{card.name}</h2>
          <FactsForm card={card} lookups={lib.lookups} account={account} history={lib.history} busy={busy} errors={errors} onSubmit={generate} onCancel={() => { setCard(null); setErrors([]); }} />
        </div>
      ) : lib && (
        <>
          {rec && (
            <section className="mt-6" data-testid="letters-recommended">
              <h2 className="text-lg font-semibold text-slate-900">Recommended for this account</h2>
              <ul className="mt-3"><LetterCard card={rec} lookups={lib.lookups} onUse={setCard} highlight /></ul>
            </section>
          )}
          {GROUPS.map(([r, title]) => (
            <section key={r} className="mt-8" data-testid={`letters-group-${r}`}>
              <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
              <ul className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
                {lib.cards.filter((c) => c.round === r && c.letter_key !== lib.recommended).map((c) => <LetterCard key={c.letter_key} card={c} lookups={lib.lookups} onUse={setCard} />)}
              </ul>
            </section>
          ))}
        </>
      )}
    </AppShell>
  );
}
